import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import Voltar from "@/components/Voltar";
import { MANDALA, fmtDateInput } from "@/lib/format";
import { toCents, formatBRL } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, ExternalLink } from "@/components/icons";

const INPUT =
  "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";
const LABEL = "block text-sm font-medium text-slate-700 mb-1.5";

// Etapas permitidas na CRIAÇÃO (item 7 do pedido) e probabilidade automática (item 8).
const ETAPAS_CRIACAO = ["Prospecção", "Qualificação"];
const PROB_POR_ETAPA = {
  "Prospecção": 10,
  "Qualificação": 20,
  "Testes": 40,
  "Proposta": 60,
  "Negociação": 80,
};

// Lista de tipos da "Próxima ação" (item 11). Idem "Registrar atividade".
const TIPOS_PROX_ACAO = [
  "Visita", "Ligação", "WhatsApp", "E-mail", "Reunião",
  "Teste/Demonstração", "Enviar proposta", "Fechamento",
];

// Etapa considerada "aberta" (para o aviso de duplicidade — item 4).
const ETAPAS_ABERTAS = new Set(["Prospecção", "Qualificação", "Testes", "Proposta", "Negociação"]);

// hoje no fuso local, em YYYY-MM-DD, para o `min` do input date.
const hojeLocalISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function DealForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const editing = !!id;
  const clienteIdBloqueado = sp.get("cliente_id") || "";
  const clienteNomeBloqueado = sp.get("cliente_nome") || "";
  const clientePreselecionado = !!clienteIdBloqueado; // aberto de dentro do Cliente

  const podeEscolherResponsavel = ["manager", "director", "admin", "diretor_geral"].includes(user?.role);

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [equipe, setEquipe] = useState([]); // vendedores da equipe (gerente/diretor)
  const [dealsAbertos, setDealsAbertos] = useState([]); // do cliente selecionado
  const [duplicidadeOpen, setDuplicidadeOpen] = useState(false);
  const [dealExistente, setDealExistente] = useState(null);

  const [form, setForm] = useState({
    titulo: "",
    cliente_id: clienteIdBloqueado,
    cliente_nome: clienteNomeBloqueado,
    valor: 0,
    stage: "Prospecção",
    responsavel_id: "",   // usado quando gerente/diretor escolhe
    responsavel: "",      // nome do responsável final
    data_prevista: "",
    notas: "",
    fase_mandala: "",
  });
  // Bloco da "Próxima ação" (item 11).
  const [prox, setProx] = useState({ tipo: TIPOS_PROX_ACAO[0], descricao: "", data: "" });

  const prob = PROB_POR_ETAPA[form.stage] ?? 20;

  // Carrega clientes (para o select), equipe (para gerente/diretor) e — se
  // edição — a oportunidade atual.
  useEffect(() => {
    api.get("/clientes").then((r) => setClientes(r.data || [])).catch(() => {});
    if (podeEscolherResponsavel) {
      api.get("/equipe").then((r) => {
        const raw = r.data || [];
        const role = user?.role;
        const uid = user?.id;
        // Admin e Diretor Geral podem escolher qualquer vendedor da empresa.
        // Gerente/Diretor: só os vendedores da sua equipe direta (item 10 do pedido).
        const sellers = raw.filter((m) => {
          if (m.role !== "seller") return false;
          if (role === "admin" || role === "diretor_geral") return true;
          return m.manager_id === uid || m.director_id === uid;
        });
        setEquipe(sellers);
      }).catch(() => {});
    }
    if (editing) {
      api.get("/deals").then((r) => {
        const d = (r.data || []).find((x) => x.id === id);
        if (d) {
          setForm((f) => ({
            ...f, ...d,
            valor: Math.round((Number(d.valor) || 0) * 100),
            data_prevista: fmtDateInput(d.data_prevista),
            fase_mandala: d.fase_mandala || "",
            // Na edição, permite qualquer etapa; a probabilidade é derivada.
          }));
        }
      }).finally(() => setLoading(false));
    }
  }, [id, editing, podeEscolherResponsavel, user?.id]);

  // Ao mudar (ou receber) cliente, busca oportunidades ABERTAS desse cliente
  // pra alimentar o aviso de duplicidade (item 4).
  useEffect(() => {
    if (!form.cliente_id || editing) { setDealsAbertos([]); return; }
    api.get("/deals").then((r) => {
      const abertas = (r.data || []).filter(
        (d) => d.cliente_id === form.cliente_id && ETAPAS_ABERTAS.has(d.stage)
      );
      setDealsAbertos(abertas);
    }).catch(() => setDealsAbertos([]));
  }, [form.cliente_id, editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onCliente = (e) => {
    const cid = e.target.value;
    const c = clientes.find((x) => x.id === cid);
    setForm((f) => ({ ...f, cliente_id: cid, cliente_nome: c?.nome || "" }));
  };

  const clienteNomeAtual = useMemo(() => {
    if (clientePreselecionado) return clienteNomeBloqueado;
    if (!form.cliente_id) return "";
    const c = clientes.find((x) => x.id === form.cliente_id);
    return c?.nome || form.cliente_nome || "";
  }, [clientePreselecionado, clienteNomeBloqueado, form.cliente_id, form.cliente_nome, clientes]);

  const validarNovo = () => {
    if (!form.cliente_id) { toast.error("Selecione um cliente."); return false; }
    const valorReais = Math.round(Number(form.valor) || 0) / 100;
    if (valorReais <= 0) { toast.error("Informe o valor mensal estimado (não pode ser R$ 0)."); return false; }
    if (!ETAPAS_CRIACAO.includes(form.stage)) { toast.error("Etapa inicial deve ser Prospecção ou Qualificação."); return false; }
    if (!form.data_prevista) { toast.error("Informe a previsão de fechamento."); return false; }
    if (form.data_prevista < hojeLocalISO()) { toast.error("A previsão de fechamento não pode ser data passada."); return false; }
    if (podeEscolherResponsavel && !form.responsavel_id) { toast.error("Escolha o responsável pela oportunidade."); return false; }
    if (!prox.data) { toast.error("Informe a data/hora da próxima ação."); return false; }
    if (new Date(prox.data).toString() === "Invalid Date") { toast.error("Data/hora da próxima ação inválida."); return false; }
    if (!prox.descricao.trim()) { toast.error("Descreva a próxima ação."); return false; }
    return true;
  };

  const criarComAtividade = async () => {
    setSaving(true);
    try {
      const valorReais = Math.round(Number(form.valor) || 0) / 100;
      let responsavelNome = user?.name || "";
      if (podeEscolherResponsavel && form.responsavel_id) {
        const alvo = equipe.find((m) => m.id === form.responsavel_id);
        responsavelNome = alvo?.nome || responsavelNome;
      }
      const dealPayload = {
        titulo: (form.titulo || "").trim(),
        cliente_id: form.cliente_id,
        cliente_nome: clienteNomeAtual,
        valor: valorReais,
        stage: form.stage,
        prob,
        responsavel: responsavelNome,
        data_prevista: new Date(form.data_prevista).toISOString(),
        notas: form.notas || "",
        fase_mandala: form.fase_mandala ? Number(form.fase_mandala) : null,
      };
      const { data: deal } = await api.post("/deals", dealPayload);
      // Cria a "Próxima ação" já vinculada — vira agenda + card na oportunidade.
      try {
        await api.post("/atividades", {
          titulo: prox.descricao.trim().slice(0, 120),
          tipo: prox.tipo,
          cliente_id: form.cliente_id,
          cliente_nome: clienteNomeAtual,
          deal_id: deal.id,
          deal_titulo: deal.titulo || "",
          data: new Date(prox.data).toISOString(),
          descricao: prox.descricao.trim(),
        });
      } catch (err) {
        toast.warning("Oportunidade criada, mas a próxima ação falhou: " + errMsg(err));
      }
      toast.success("Oportunidade criada");
      navigate(`/deal/${deal.id}`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const salvarEdicao = async () => {
    // Fluxo antigo para edição, sem regras novas: mantém oportunidades já existentes intactas (item NÃO MEXER).
    const valorReais = Math.round(Number(form.valor) || 0) / 100;
    if (valorReais <= 0) { toast.error("Informe o valor da oportunidade (não pode ser R$ 0)."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        valor: valorReais,
        prob: PROB_POR_ETAPA[form.stage] ?? (Number(form.prob) || 0),
        fase_mandala: form.fase_mandala ? Number(form.fase_mandala) : null,
        data_prevista: form.data_prevista ? new Date(form.data_prevista).toISOString() : null,
      };
      await api.put(`/deals/${id}`, payload);
      toast.success("Oportunidade atualizada");
      navigate(`/deal/${id}`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (editing) return salvarEdicao();
    if (!validarNovo()) return;
    if (dealsAbertos.length > 0) {
      setDealExistente(dealsAbertos[0]);
      setDuplicidadeOpen(true);
      return;
    }
    return criarComAtividade();
  };

  if (loading) return <Loading />;

  return (
    <div className="max-w-6xl">
      <Voltar fallback="/pipeline" className="mb-4" />
      <PageHeader title={editing ? "Editar oportunidade" : "Nova oportunidade"} />

      <div className="grid lg:grid-cols-[minmax(0,40rem)_1fr] gap-8 items-start">
      <form onSubmit={submit} className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)] space-y-4" data-testid="deal-form">
        <div>
          <label className={LABEL}>Título da oportunidade (opcional)</label>
          <input
            value={form.titulo}
            onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))}
            className={INPUT}
            data-testid="deal-titulo"
            placeholder="Ex: Higienização Geral"
          />
          <p className="text-[11px] text-muted-foreground mt-1">Descreva o negócio, ex.: Higienização Geral, Lavanderia, Linha hospitalar.</p>
        </div>

        <div>
          <label className={LABEL}>Cliente</label>
          {clientePreselecionado ? (
            <div
              data-testid="deal-cliente-locked"
              className="h-11 px-3.5 rounded-lg border border-input bg-slate-50 text-sm text-navy flex items-center font-medium"
            >
              {clienteNomeBloqueado}
            </div>
          ) : (
            <>
              <select
                value={form.cliente_id}
                onChange={onCliente}
                className={INPUT}
                data-testid="deal-cliente"
                disabled={editing}
              >
                <option value="">— Selecione um cliente —</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nome}{c.empresa ? ` · ${c.empresa}` : ""}</option>
                ))}
              </select>
              {!editing && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Cliente não encontrado?{" "}
                  <Link to="/clientes" className="text-brand font-medium underline" data-testid="deal-link-clientes">
                    Cadastre em Clientes
                  </Link>.
                </p>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={LABEL}>Valor mensal estimado *</label>
            <input
              type="text"
              inputMode="numeric"
              value={formatBRL(form.valor)}
              onChange={(e) => setForm((f) => ({ ...f, valor: toCents(e.target.value) }))}
              className={INPUT}
              data-testid="deal-valor"
              placeholder="R$ 0,00"
              required
            />
          </div>
          <div>
            <label className={LABEL}>Probabilidade</label>
            <div
              data-testid="deal-prob"
              className="h-11 px-3.5 rounded-lg border border-input bg-slate-50 text-sm text-navy flex items-center font-medium"
            >
              {prob}%
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Automática pela etapa.</p>
          </div>
          <div>
            <label className={LABEL}>Etapa do Funil</label>
            <select
              value={ETAPAS_CRIACAO.includes(form.stage) ? form.stage : "Prospecção"}
              onChange={set("stage")}
              className={INPUT}
              data-testid="deal-stage"
              disabled={editing}
            >
              {ETAPAS_CRIACAO.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={LABEL}>Previsão de fechamento *</label>
            <input
              type="date"
              value={form.data_prevista}
              onChange={set("data_prevista")}
              min={hojeLocalISO()}
              className={INPUT}
              data-testid="deal-data"
              required
            />
          </div>
        </div>

        {podeEscolherResponsavel && !editing && (
          <div>
            <label className={LABEL}>Responsável *</label>
            <select
              value={form.responsavel_id}
              onChange={(e) => setForm((f) => ({ ...f, responsavel_id: e.target.value }))}
              className={INPUT}
              data-testid="deal-responsavel"
              required
            >
              <option value="">— Selecione um vendedor da equipe —</option>
              {equipe.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">Depois é possível trocar em &quot;Reatribuir&quot;.</p>
          </div>
        )}

        <div>
          <label className={LABEL}>Metodologia Allia (orientação)</label>
          <select value={form.fase_mandala} onChange={set("fase_mandala")} className={INPUT} data-testid="deal-mandala">
            <option value="">— Sem classificação —</option>
            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>Allia {n} · {MANDALA[n]}</option>)}
          </select>
        </div>

        <div>
          <label className={LABEL}>Notas</label>
          <textarea value={form.notas} onChange={set("notas")} rows={3} className={`${INPUT} h-auto py-2.5`} data-testid="deal-notas" />
        </div>

        {!editing && (
          <div className="rounded-xl border border-dashed border-brand-accent bg-brand-soft p-4 space-y-3" data-testid="deal-prox-acao-bloco">
            <div>
              <h3 className="text-sm font-semibold text-navy">Próxima ação *</h3>
              <p className="text-[11px] text-navy/70">Toda oportunidade nasce com um próximo passo. Vai para a Agenda do responsável.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Tipo</label>
                <select
                  value={prox.tipo}
                  onChange={(e) => setProx((p) => ({ ...p, tipo: e.target.value }))}
                  className={INPUT}
                  data-testid="prox-tipo"
                >
                  {TIPOS_PROX_ACAO.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Data e hora</label>
                <input
                  type="datetime-local"
                  value={prox.data}
                  onChange={(e) => setProx((p) => ({ ...p, data: e.target.value }))}
                  className={INPUT}
                  data-testid="prox-data"
                  required
                />
              </div>
            </div>
            <div>
              <label className={LABEL}>Descrição curta</label>
              <input
                value={prox.descricao}
                onChange={(e) => setProx((p) => ({ ...p, descricao: e.target.value }))}
                className={INPUT}
                placeholder="Ex.: Visita técnica para levantamento"
                data-testid="prox-descricao"
                maxLength={120}
                required
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate(-1)} className="h-11 px-5 rounded-lg border border-input text-sm font-medium hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="save-deal-btn"
            className="h-11 px-6 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Salvar" : "Criar oportunidade"}
          </button>
        </div>
      </form>

        <aside className="hidden lg:flex flex-col items-center sticky top-6" data-testid="deal-mandala-aside">
          <img src={`${process.env.PUBLIC_URL}/mandala-allia.png`} alt="Metodologia Allia" className="w-full max-w-md rounded-2xl shadow-[0_10px_40px_rgb(0,0,0,0.08)]" />
          <p className="text-sm text-muted-foreground text-center mt-4 max-w-sm leading-relaxed">
            <span className="font-semibold text-navy">Metodologia Allia</span> — diagnóstico e processos, produtos profissionais, treinamento e conscientização, gestão e controle.
          </p>
        </aside>
      </div>

      {/* Aviso de duplicidade (item 4) */}
      <Dialog open={duplicidadeOpen} onOpenChange={setDuplicidadeOpen}>
        <DialogContent data-testid="deal-duplicidade-dialog">
          <DialogHeader>
            <DialogTitle>Já existe uma oportunidade aberta</DialogTitle>
            <DialogDescription>
              {clienteNomeAtual} já tem a oportunidade aberta{" "}
              <span className="font-semibold text-navy">{dealExistente?.titulo || "(sem título)"}</span>,
              na etapa <span className="font-semibold text-navy">{dealExistente?.stage}</span>.
              Visita, teste e negociação são registrados dentro dela.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => { setDuplicidadeOpen(false); if (dealExistente) navigate(`/deal/${dealExistente.id}`); }}
              data-testid="deal-abrir-existente"
              className="h-10 px-5 rounded-lg border border-input text-sm font-semibold hover:bg-slate-50 flex items-center gap-1.5"
            >
              <ExternalLink className="w-4 h-4" /> Abrir a oportunidade existente
            </button>
            <button
              type="button"
              onClick={() => { setDuplicidadeOpen(false); criarComAtividade(); }}
              data-testid="deal-criar-nova-mesmo-assim"
              className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover"
            >
              É outro negócio, criar nova
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
